import React, { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CloseRounded, KeyOutlined } from '@mui/icons-material';
import { AUTH_KEYS, NS } from '../../../i18n/constants/keys';
import { TextInput } from '../../../ui/input/text_input/text_input';

type ResetPasswordProps = {
  handleSubmit: (event: FormEvent<HTMLFormElement>, email: string) => void;
  onClose: () => void;
  open: boolean;
};

export const ResetPassword: React.FC<ResetPasswordProps> = ({
  handleSubmit,
  onClose,
  open,
}) => {
  const { t } = useTranslation(NS.auth);
  const [email, setEmail] = useState<string>('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          p: { xs: 2, sm: 3.5 },
          bgcolor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      {/* Decorative gradient glow */}
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 140,
          height: 140,
          borderRadius: '50%',
          bgcolor: 'rgba(16, 185, 129, 0.15)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />

      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: '#94a3b8',
          bgcolor: '#f8fafc',
          '&:hover': { bgcolor: '#f1f5f9', color: '#334155' },
        }}
      >
        <CloseRounded fontSize="small" />
      </IconButton>

      <DialogContent sx={{ p: 0, mt: 1 }}>
        <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              bgcolor: '#ecfdf5',
              border: '1px solid #d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#059669',
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.12)',
            }}
          >
            <KeyOutlined sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
              {t(AUTH_KEYS.resetPassword.title)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.75, maxWidth: 300 }}>
              {t(AUTH_KEYS.resetPassword.subtitle)}
            </Typography>
          </Box>
        </Stack>

        <Box
          component="form"
          onSubmit={(event: FormEvent<HTMLFormElement>) => handleSubmit(event, email)}
          data-testid="login-form"
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
          <TextInput
            type="email"
            label={t(AUTH_KEYS.resetPassword.emailAddress)}
            name="email"
            data-testid="email"
            variant="pill"
            placeholder="name@company.com"
            value={email}
            onChange={handleChange}
            required
            autoFocus
          />

          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
            <Button
              fullWidth
              onClick={onClose}
              type="button"
              variant="outlined"
              sx={{
                borderRadius: '9999px',
                py: 1.25,
                borderColor: '#e2e8f0',
                color: '#475569',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#cbd5e1',
                  bgcolor: '#f8fafc',
                },
              }}
            >
              {t(AUTH_KEYS.resetPassword.cancel)}
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                borderRadius: '9999px',
                py: 1.25,
                bgcolor: '#050508',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  bgcolor: '#1e293b',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                },
              }}
            >
              {t(AUTH_KEYS.resetPassword.sendResetMail)}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
